import { Component, OnInit, ViewChild } from '@angular/core';
import { ApiDataSource } from "../../utils/api-data-source";
import { UtilsService } from "../../shared/utils.service";
import { AppService } from "../../app.service";
import { ApiRequestService } from "../../shared/api-request.service";
import { MatDialog } from "@angular/material/dialog";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { tap } from "rxjs/operators";
import { ToolboxTalksTemplatesViewComponent } from "../toolbox-talks-templates-view/toolbox-talks-templates-view.component";
import { ToolboxTalksTemplatesFilterComponent } from "../toolbox-talks-templates-filter/toolbox-talks-templates-filter.component";
import { ToolboxTalksTemplatesEditComponent } from "../toolbox-talks-templates-edit/toolbox-talks-templates-edit.component";
import { ToolboxTalkTemplateModel } from '../../models/toolbox-talk-template.model';
import { UserPublicProfileComponent } from "../../shared/user-public-profile/user-public-profile.component";
import { ToolboxTalksTemplatesSnippetsSelectorComponent } from '../toolbox-talks-templates-snippets-selector/toolbox-talks-templates-snippets-selector.component';

@Component({
  selector: 'app-toolbox-talks-templates',
  templateUrl: './toolbox-talks-templates.component.html',
  styleUrls: ['./toolbox-talks-templates.component.scss']
})
export class ToolboxTalksTemplatesComponent implements OnInit {

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  dataSource = new ToolboxTalksTemplatesDataSource(this.app, this.api);

  displayedColumns = [
    'select',
    'id',
    'name',
    'template_type',
    'created_by_user',
    'updated_by_user',
    'date_created',
    'buttons'
  ];

  constructor(
    public utils: UtilsService,
    public app: AppService,
    private api: ApiRequestService,
    private dialog: MatDialog
  ) { }

  ngOnInit() {
    this.dataSource.getData(true);
  }

  ngAfterViewInit() {
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.order = this.sort.direction;

      if (this.sort.active === "date_created_UTC") {
        this.dataSource.order_by = "date_created";
      } else {
        this.dataSource.order_by = this.sort.active;
      }

      this.dataSource.getData(false);
    });

    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  onAdd(template_type: 'template' | 'snippet' = 'template') {
    const tt_template = new ToolboxTalkTemplateModel();
    tt_template.template_type = template_type;
    this.onEdit(tt_template);
  }

  onEdit(tt_template: ToolboxTalkTemplateModel) {
    this.dialog
      .open(ToolboxTalksTemplatesEditComponent, {
        width: '900px',
        data: {
          toolbox_talk: tt_template
        }
      })
      .afterClosed()
      .subscribe(() => {
        this.dataSource.getData(true);
      });
  }

  onBuildFromSnippets() {
    this.utils.showComponentDialog(
      ToolboxTalksTemplatesSnippetsSelectorComponent,
      {
        multiple: true,
        global: false
      },
      {
        width: '1200px'
      },
      (selectedIds: number[]) => {
        if (Array.isArray(selectedIds) && selectedIds.length > 0) {
          this.api.makeRequest(
            'get',
            `v1/toolbox-talks-templates/${selectedIds.join(',')}`
          )
            .then((response: any) => {
              const templates = response.data as ToolboxTalkTemplateModel[];

              const mergedSnippets = this.mergeSnippets(templates);
              mergedSnippets.template_type = 'template';
              this.onEdit(mergedSnippets);
            })
            .catch((err) => this.utils.handleAPIErrors(err));
        }
      }
    );
  }

  mergeSnippets(templates: ToolboxTalkTemplateModel[]): ToolboxTalkTemplateModel {
    const mergedAgenda = templates
      .map(t => `<h3>${t.name}</h3>\n\n${t.agenda}`)
      .join('\n\n');

    const toolboxTalkTemplate = new ToolboxTalkTemplateModel();
    toolboxTalkTemplate.agenda = mergedAgenda;

    return toolboxTalkTemplate;
  }

  onClone(tt_template: ToolboxTalkTemplateModel) {
    const newTTTemplate = new ToolboxTalkTemplateModel();
    newTTTemplate.cloneFrom(tt_template);
    this.onEdit(newTTTemplate);
  }

  onView(id: number) {
    this.utils.showComponentDialog(
      ToolboxTalksTemplatesViewComponent,
      id,
      { width: '700px' },
      () => {
        this.dataSource.getData(true);
      }
    );
  }

  onRemoveSelected() {
    this.utils.showModal(
      'Delete ' + this.utils.getLangTerm('toolbox-talks.plural', 'Toolbox Talks') + ' Templates',
      `Are you sure you want to delete ${this.dataSource.selection.selected.length} ${this.utils.getLangTerm('toolbox-talks.plural', 'Toolbox Talks')} templates?`,
      () => {
        this.remove(this.dataSource.selection.selected);
      }
    );
  }

  onRemove(tt_template: ToolboxTalkTemplateModel) {
    this.utils.showModal(
      'Delete ' + this.utils.getLangTerm('toolbox-talks.plural', 'Toolbox Talks') + ' Template',
      `Are you sure you want to delete the "${tt_template.name}" ${this.utils.getLangTerm('toolbox-talks.plural', 'Toolbox Talks')} template?`,
      () => {
        this.remove([tt_template.id]);
      }
    );
  }

  private remove(ids: number[]) {
    this.api.makeRequest('delete', `v1/toolbox-talks/${ids.join(',')}`, {},{
      'template': 'true'
    })
      .then(() => {
        this.utils.showToast('The ' + this.utils.getLangTerm('toolbox-talks.plural', 'Toolbox Talks') + ' templates have been deleted.');
        this.dataSource.selection.clear();
        this.dataSource.getData(false);
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  onFilter() {
    this.utils.showComponentDialog(
      ToolboxTalksTemplatesFilterComponent,
      {
        template_type: this.dataSource.template_type,
        employee_ids: this.dataSource.employee_ids,
        date_range: this.dataSource.date_range
      },
      {
        width: '900px'
      },
      (results) => {
        if (typeof results !== 'undefined') {
          this.dataSource.employee_ids = results.employee_ids ? results.employee_ids : this.dataSource.employee_ids;
          this.dataSource.date_range = results.date_range ? results.date_range : this.dataSource.date_range;

          if (typeof results.template_type !== 'undefined') {
            this.dataSource.template_type = results.template_type;
          }
          this.dataSource.getData();
        }
      }
    );
  }

  onUserPublicView(hash: string) {
    this.utils.showComponentDialog(
      UserPublicProfileComponent,
      hash,
      { width: '90%' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
        this.dataSource.getData();
      }
    );
  }

  onExport(type: string = 'pdf', id?: number) {

    let ids: number[] = [];
    if (id) {
      ids.push(id);
    } else {
      ids.push(...this.dataSource.selection.selected);
    }

    this.api.makeDownloadRequest(`v1/toolbox-talks/export/${type}` + (ids.length > 0 ? ('/' + ids.join(',')) : ''), {}, {
      date_range: this.dataSource.date_range && this.dataSource.date_range.length > 1 ? [
        this.dataSource.date_range[0].getTime() / 1000,
        this.dataSource.date_range[1].getTime() / 1000
      ].join(',') : "",
      sort_by: this.dataSource.order_by,
      sort_order: this.dataSource.order,
      template_type: "all"
    })
      .then((response) => {

        // Get the current date object
        const date = new Date();

        // Create object url to handle file downloads
        const data = window.URL.createObjectURL(response);

        // Create a download link
        const downloadLink = document.createElement('a');
        downloadLink.href = data;
        downloadLink.download = (this.utils.getLangTerm('toolbox-talks.plural', 'Toolbox Talks')) + ` - ${date.getFullYear()}${(date.getMonth() + 1)}${date.getDate()}.${type}`;
        // Initiate the download
        downloadLink.click();

        // For Firefox it is necessary to delay revoking the ObjectURL
        setTimeout(function () {
          window.URL.revokeObjectURL(data);
        }, 300); // Minimum 300 miliseconds
      })
      .catch((errorResponse) => {
        this.utils.showModal('Error', errorResponse.message);
      });
  }
}

export class ToolboxTalksTemplatesDataSource extends ApiDataSource {
  order_by = 'id';
  order = 'desc';
  searchBy = 'name';

  employee_ids = [];
  date_range: Date[] = [];
  template_type = "";

  getData(resetOffset: boolean = false) {
    this.makeRequest('v1/toolbox-talks', resetOffset, {
      searchBy: this.searchBy,
      employee_ids: this.employee_ids.length ? this.employee_ids.join(',') : "",
      template_type: this.template_type.length ? this.template_type : "all",
      date_range: this.date_range && this.date_range.length > 1 ? [
        this.date_range[0].getTime() / 1000,
        this.date_range[1].getTime() / 1000
      ].join(',') : ""
    });
  }
}
